import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Space, Button, Input, Select, message, Pagination, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined, UserOutlined, GlobalOutlined, MobileOutlined, DesktopOutlined } from '@ant-design/icons';
import { loginLogAPI } from '../services/api';
import moment from 'moment';
const { Option } = Select;

const LoginLogs = ({ isAdmin = false }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    username: '',
    userId: '',
    device: '',
    browser: '',
    os: ''
  });

  const fetchLogs = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        ...filters
      };

      const response = isAdmin 
        ? await loginLogAPI.getLoginLogs(params)
        : await loginLogAPI.getMyLoginLogs(params);

      setLogs(response.data.data);
      setPagination({
        current: response.data.pagination.page,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.total
      });
    } catch (error) {
      message.error('获取登录记录失败');
      console.error('获取登录记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(1, pagination.pageSize);
  };

  const handleReset = () => {
    setFilters({
      username: '',
      userId: '',
      device: '',
      browser: '',
      os: ''
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(1, pagination.pageSize);
  };

  const handleTableChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
    fetchLogs(page, pageSize);
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'Mobile':
        return <MobileOutlined style={{ color: '#1890ff' }} />;
      case 'Tablet':
        return <MobileOutlined style={{ color: '#52c41a' }} />;
      case 'Desktop':
        return <DesktopOutlined style={{ color: '#722ed1' }} />;
      default:
        return <UserOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getStatusTag = (isActive, logoutTime) => {
    if (isActive) {
      return <Tag color="green">在线</Tag>;
    } else if (logoutTime) {
      return <Tag color="default">已登出</Tag>;
    } else {
      return <Tag color="orange">未知</Tag>;
    }
  };

  const columns = [
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <span>{text}</span>
          {isAdmin && <span style={{ color: '#999', fontSize: '12px' }}>(ID: {record.userId})</span>}
        </Space>
      )
    },
    {
      title: '设备信息',
      key: 'device',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Space>
            {getDeviceIcon(record.device)}
            <span>{record.device || 'Unknown'}</span>
          </Space>
          <Space>
            <GlobalOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: '12px' }}>{record.browser || 'Unknown'}</span>
          </Space>
          <span style={{ fontSize: '12px', color: '#666' }}>{record.os || 'Unknown'}</span>
        </Space>
      )
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '登录时间',
      dataIndex: 'loginTime',
      key: 'loginTime',
      width: 150,
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '登出时间',
      dataIndex: 'logoutTime',
      key: 'logoutTime',
      width: 150,
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_, record) => getStatusTag(record.isActive, record.logoutTime)
    }
  ];

  if (isAdmin) {
    columns.splice(1, 0, {
      title: '会话ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: 100,
      render: (text) => text ? text.substring(0, 8) + '...' : '-'
    });
  }

  return (
    <Card 
      title={
        <Space>
          <UserOutlined />
          {isAdmin ? '登录记录管理' : '我的登录记录'}
        </Space>
      }
      extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => fetchLogs(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            刷新
          </Button>
        </Space>
      }
    >
      {/* 搜索筛选区域 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="用户名"
            value={filters.username}
            onChange={(e) => setFilters(prev => ({ ...prev, username: e.target.value }))}
            style={{ width: 120 }}
          />
          {isAdmin && (
            <Input
              placeholder="用户ID"
              value={filters.userId}
              onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
              style={{ width: 100 }}
            />
          )}
          <Select
            placeholder="设备类型"
            value={filters.device}
            onChange={(value) => setFilters(prev => ({ ...prev, device: value }))}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="Desktop">桌面</Option>
            <Option value="Mobile">手机</Option>
            <Option value="Tablet">平板</Option>
          </Select>
          <Select
            placeholder="浏览器"
            value={filters.browser}
            onChange={(value) => setFilters(prev => ({ ...prev, browser: value }))}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="Chrome">Chrome</Option>
            <Option value="Firefox">Firefox</Option>
            <Option value="Safari">Safari</Option>
            <Option value="Edge">Edge</Option>
            <Option value="Opera">Opera</Option>
          </Select>
          <Button 
            type="primary" 
            icon={<SearchOutlined />} 
            onClick={handleSearch}
            loading={loading}
          >
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </Card>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 800 }}
        size="small"
      />

      {/* 分页 */}
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
          onChange={handleTableChange}
          onShowSizeChange={handleTableChange}
          pageSizeOptions={['10', '20', '50', '100']}
        />
      </div>
    </Card>
  );
};

export default LoginLogs;
